/**
 * Zustand der Ablage-Auswahl im E-Mail-Fenster (Kundenfeedback 24.10.2026,
 * IV.2): Ordner der Akte, Einstellung der Organisation als Vorbelegung und
 * die Auswahl des Absenders. Logik und Ausführung: sendFiling.ts.
 */
import type { FilingChoice } from './sendFiling'
import { computed, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/authStore'
import { loadEmailFilingSettings } from '../chat/emailFilingApi'
import { useFolderTargets } from '../chat/useFolderTargets'
import { filingPlan, filingSummary, filingTooltip, initialFilingChoice } from './sendFiling'

export function useSendFiling (caseId: () => number | null, attachmentCount: () => number) {
  const folders = useFolderTargets(caseId)
  const autoFile = ref<boolean | null>(null)
  const choice = ref<FilingChoice>(initialFilingChoice(null, ''))
  // Hat der Absender selbst gewählt? Dann überschreibt die Vorbelegung nichts mehr.
  const touched = ref(false)
  let settingsRequested = false

  async function loadSettings () {
    if (settingsRequested) {
      return
    }
    settingsRequested = true
    const orgId = Number(useAuthStore().idOrganization) || 0
    if (!orgId) {
      return
    }
    try {
      autoFile.value = (await loadEmailFilingSettings(orgId)).autoFileSentEmails
    } catch {
      // Unbekannt: Die Organisationseinstellung entscheidet wie bisher.
      autoFile.value = null
      settingsRequested = false
    }
  }

  function applyDefault () {
    if (touched.value) {
      return
    }
    choice.value = initialFilingChoice(autoFile.value, folders.defaultFolder.value)
  }

  watch(caseId, id => {
    touched.value = false
    if (id) {
      void loadSettings()
    }
    applyDefault()
  }, { immediate: true })
  watch([autoFile, () => folders.defaultFolder.value], applyDefault)

  function update (patch: Partial<FilingChoice>) {
    touched.value = true
    const next = { ...choice.value, ...patch }
    // Wer einen Ordner wählt, obwohl die Einstellung noch unbekannt ist,
    // hat sich für das Ablegen entschieden.
    if (next.mode === 'auto') {
      next.mode = 'folder'
    }
    if (next.mode === 'folder' && !next.folder) {
      next.folder = folders.defaultFolder.value
    }
    choice.value = next
  }

  /** Neues E-Mail-Fenster: wieder mit der Vorbelegung beginnen. */
  function reset () {
    touched.value = false
    applyDefault()
  }

  const plan = computed(() => filingPlan(choice.value, attachmentCount()))
  const summary = computed(() => filingSummary(choice.value, folders.targets.value, attachmentCount()))
  const tooltip = computed(() => filingTooltip(choice.value, folders.targets.value, attachmentCount()))

  return { folders, autoFile, choice, plan, summary, tooltip, update, reset }
}

export type SendFilingController = ReturnType<typeof useSendFiling>
